import { useEffect, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { config, ApiError } from "../services/api";
import { Haulier, HAULIER_LABELS } from "../types";
import type { FuelEscalatorStep, HaulierConfig } from "../types";

const HAULIER_TABS: Haulier[] = [Haulier.BOX_LOGISTICS, Haulier.PALLETWAYS, Haulier.TPN];

function emptyForm() {
  return {
    directRate: "",
    postcodeAreas: "",
    fuelShare: "",
    escalatorText: "",
  };
}

function configToForm(cfg: HaulierConfig) {
  return {
    directRate: cfg.direct_rate_per_pallet != null ? String(cfg.direct_rate_per_pallet) : "",
    postcodeAreas: (cfg.direct_postcode_areas ?? []).join(" "),
    fuelShare: cfg.fuel_surcharge_share != null ? String(cfg.fuel_surcharge_share) : "",
    escalatorText: (cfg.fuel_escalator ?? [])
      .map((step) => `${step.pence} ${step.pct}`)
      .join("\n"),
  };
}

/** Parses "pence pct" lines. Throws with a human-readable message on bad input. */
function parseEscalator(text: string): FuelEscalatorStep[] {
  const lines = text
    .split("\n")
    .map((l) => l.trim())
    .filter((l) => l.length > 0);

  const steps: FuelEscalatorStep[] = [];
  for (const line of lines) {
    const parts = line.split(/\s+/);
    if (parts.length !== 2) {
      throw new Error(`Invalid escalator line "${line}" — expected "pence pct".`);
    }
    const pence = Number(parts[0]);
    const pct = Number(parts[1]);
    if (!Number.isFinite(pence) || !Number.isFinite(pct)) {
      throw new Error(`Invalid escalator line "${line}" — expected two numbers.`);
    }
    if (pence < 0 || pct < 0) {
      throw new Error(`Invalid escalator line "${line}" — pence and pct must both be ≥ 0.`);
    }
    steps.push({ pence, pct });
  }

  for (let i = 1; i < steps.length; i++) {
    if (steps[i].pence <= steps[i - 1].pence) {
      throw new Error(
        `Escalator pence values must be strictly ascending (line ${i + 1}: ${steps[i].pence} is not greater than ${steps[i - 1].pence}).`,
      );
    }
  }

  return steps;
}

/** Parses/validates postcode areas. Throws with a human-readable message on bad input. */
function parsePostcodeAreas(text: string): string[] {
  const areas = text
    .split(/[\s,]+/)
    .map((s) => s.trim().toUpperCase())
    .filter((s) => s.length > 0);

  for (const area of areas) {
    if (!/^[A-Z]{1,2}$/.test(area)) {
      throw new Error(`Invalid postcode area "${area}" — expected 1–2 letters (e.g. "E" or "EC").`);
    }
  }

  return areas;
}

/** Parses the direct-rate field. Blank means null. Throws with a human-readable message on bad input. */
function parseDirectRate(text: string): number | null {
  const trimmed = text.trim();
  if (trimmed === "") return null;
  const value = Number(trimmed);
  if (!Number.isFinite(value) || value < 0) {
    throw new Error(`Invalid direct rate "${text}" — expected a number ≥ 0, or blank.`);
  }
  return value;
}

/** Parses the fuel surcharge share field. Blank means null. Throws with a human-readable message on bad input. */
function parseFuelShare(text: string): number | null {
  const trimmed = text.trim();
  if (trimmed === "") return null;
  const value = Number(trimmed);
  if (!Number.isFinite(value) || value < 0 || value > 1) {
    throw new Error(`Invalid fuel surcharge share "${text}" — expected a number between 0 and 1, or blank.`);
  }
  return value;
}

export default function HaulierSettingsPage() {
  const queryClient = useQueryClient();
  const [haulier, setHaulier] = useState<Haulier>(Haulier.BOX_LOGISTICS);
  const [form, setForm] = useState(emptyForm());
  const [feedback, setFeedback] = useState<{ type: "success" | "error"; message: string } | null>(null);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["haulierConfig", haulier],
    queryFn: () => config.getHaulierConfig(haulier),
    retry: false,
    refetchOnWindowFocus: false,
  });

  const notFound = isError && error instanceof ApiError && error.status === 404;
  const loadFailed = isError && !notFound;

  // Tracks which haulier the form currently reflects server state for, so a
  // background refetch (e.g. after invalidation) never clobbers in-progress edits.
  // Only load from `data`/`notFound` the first time they settle for the selected haulier.
  const loadedForHaulierRef = useRef<Haulier | null>(null);

  useEffect(() => {
    if (loadedForHaulierRef.current === haulier) return;
    if (data) {
      setForm(configToForm(data));
      loadedForHaulierRef.current = haulier;
    } else if (notFound) {
      setForm(emptyForm());
      loadedForHaulierRef.current = haulier;
    }
  }, [data, notFound, haulier]);

  const saveMutation = useMutation({
    mutationFn: (cfg: Omit<HaulierConfig, "haulier" | "updated_by" | "updated_at">) =>
      config.saveHaulierConfig(haulier, cfg),
    onSuccess: (saved) => {
      queryClient.invalidateQueries({ queryKey: ["haulierConfig", haulier] });
      setForm(configToForm(saved));
      loadedForHaulierRef.current = haulier;
      setFeedback({ type: "success", message: "Saved haulier settings successfully." });
      setTimeout(() => setFeedback(null), 4000);
    },
    onError: (err: Error) => {
      setFeedback({ type: "error", message: err.message });
      setTimeout(() => setFeedback(null), 6000);
    },
  });

  function handleSave() {
    let directRate: number | null;
    let fuelShare: number | null;
    let postcodeAreas: string[];
    let escalator: FuelEscalatorStep[];
    try {
      directRate = parseDirectRate(form.directRate);
      fuelShare = parseFuelShare(form.fuelShare);
      postcodeAreas = parsePostcodeAreas(form.postcodeAreas);
      escalator = parseEscalator(form.escalatorText);
    } catch (err) {
      setFeedback({ type: "error", message: err instanceof Error ? err.message : "Invalid input." });
      setTimeout(() => setFeedback(null), 6000);
      return;
    }

    saveMutation.mutate({
      direct_rate_per_pallet: directRate,
      direct_postcode_areas: postcodeAreas,
      fuel_surcharge_share: fuelShare,
      fuel_escalator: escalator,
    });
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Haulier Settings</h1>
        <p className="mt-1 text-sm text-gray-600">
          Configure direct-rate postcodes and the fuel surcharge escalator per haulier.
        </p>
      </div>

      {/* Haulier tabs */}
      <div className="flex gap-1.5">
        {HAULIER_TABS.map((h) => (
          <button
            key={h}
            onClick={() => setHaulier(h)}
            className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
              haulier === h
                ? "bg-blue-600 text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            {HAULIER_LABELS[h]}
          </button>
        ))}
      </div>

      {feedback && (
        <div
          className={`rounded-lg border px-4 py-3 text-sm ${
            feedback.type === "success"
              ? "border-emerald-200 bg-emerald-50 text-emerald-700"
              : "border-red-200 bg-red-50 text-red-700"
          }`}
        >
          {feedback.message}
        </div>
      )}

      {isLoading && (
        <div className="flex items-center justify-center py-20">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent" />
        </div>
      )}

      {loadFailed && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          Failed to load settings for {HAULIER_LABELS[haulier]}. Please try refreshing the page.
        </div>
      )}

      {!isLoading && !loadFailed && (
        <section className="rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="border-b border-gray-200 px-6 py-4">
            <h2 className="text-lg font-semibold text-gray-900">
              {HAULIER_LABELS[haulier]}
            </h2>
          </div>
          <div className="space-y-5 p-6">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Direct rate per pallet (&pound;)
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={form.directRate}
                onChange={(e) => setForm((f) => ({ ...f, directRate: e.target.value }))}
                placeholder="Leave blank if not applicable"
                className="mt-1 block w-64 rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Direct-rate postcode areas
              </label>
              <textarea
                value={form.postcodeAreas}
                onChange={(e) => setForm((f) => ({ ...f, postcodeAreas: e.target.value }))}
                placeholder="e.g. E EC N NW SE SW W WC BR CR DA EN HA IG KT RM SM TW UB WD"
                rows={3}
                className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 font-mono text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              <p className="mt-1 text-xs text-gray-500">
                Space or comma separated. Uppercased automatically on save.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Fuel surcharge share
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                max="1"
                value={form.fuelShare}
                onChange={(e) => setForm((f) => ({ ...f, fuelShare: e.target.value }))}
                placeholder="e.g. 0.5"
                className="mt-1 block w-64 rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Fuel escalator table
              </label>
              <textarea
                value={form.escalatorText}
                onChange={(e) => setForm((f) => ({ ...f, escalatorText: e.target.value }))}
                placeholder={"One \"pence pct\" pair per line, e.g.\n133 9.4\n134 9.7"}
                rows={10}
                className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 font-mono text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              <p className="mt-1 text-xs text-gray-500">
                One pence/litre &rarr; percentage pair per line. Pence values must be in ascending order.
              </p>
            </div>

            <div className="flex items-center gap-3 pt-2">
              <button
                onClick={handleSave}
                disabled={saveMutation.isPending}
                className="rounded-lg bg-blue-600 px-5 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:opacity-50"
              >
                {saveMutation.isPending ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
